import { useState, useEffect } from 'react'
import { Dropdown, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { tenantsApi } from '../../services/api'

interface Tenant {
  id: string
  name: string
  domain: string
  is_active: boolean
}

interface HeaderProps {
  onToggleSidebar: () => void
}

export default function Header({ onToggleSidebar }: HeaderProps) {
  const { user, logout, selectedTenant, setSelectedTenant } = useAuthStore()
  const navigate = useNavigate()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loadingTenants, setLoadingTenants] = useState(false)

  const isSuperAdmin = user?.role === 'super_admin'

  // Load tenants for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      loadTenants()
    }
  }, [isSuperAdmin])

  const loadTenants = async () => {
    setLoadingTenants(true)
    try {
      const response = await tenantsApi.list()
      const tenantList = response.data.items || []
      setTenants(tenantList)

      // Auto-select first tenant if none selected
      if (!selectedTenant && tenantList.length > 0) {
        setSelectedTenant(tenantList[0])
      }
    } catch (error) {
      console.error('Failed to load tenants:', error)
    } finally {
      setLoadingTenants(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleTenantSelect = (tenant: Tenant) => {
    if (selectedTenant?.id !== tenant.id) {
      setSelectedTenant(tenant)
      // Reload the page to fetch data for the new tenant
      window.location.reload()
    }
  }

  return (
    <header className="header">
      <div className="d-flex align-items-center gap-3">
        <button className="btn btn-link d-md-none p-0" onClick={onToggleSidebar}>
          <i className="bi bi-list fs-3"></i>
        </button>

        {/* Tenant Selector for Super Admin */}
        {isSuperAdmin && (
          <Dropdown>
            <Dropdown.Toggle
              variant={selectedTenant ? 'primary' : 'warning'}
              className="d-flex align-items-center gap-2"
            >
              <i className="bi bi-building"></i>
              {loadingTenants ? (
                <span>Laden...</span>
              ) : selectedTenant ? (
                <span>{selectedTenant.name}</span>
              ) : (
                <span>Mandant wählen</span>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>Mandant auswählen</Dropdown.Header>
              <Dropdown.Divider />
              {tenants.length === 0 ? (
                <Dropdown.Item disabled>Keine Mandanten vorhanden</Dropdown.Item>
              ) : (
                tenants.map(tenant => (
                  <Dropdown.Item
                    key={tenant.id}
                    active={selectedTenant?.id === tenant.id}
                    onClick={() => handleTenantSelect(tenant)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{tenant.name}</span>
                    {!tenant.is_active && (
                      <Badge bg="secondary" className="ms-2">Inaktiv</Badge>
                    )}
                  </Dropdown.Item>
                ))
              )}
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => navigate('/tenants')}>
                <i className="bi bi-gear me-2"></i>
                Mandanten verwalten
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}

        {/* Show tenant name for tenant_admin users */}
        {!isSuperAdmin && user?.tenant_id && (
          <Badge bg="info" className="d-flex align-items-center gap-1 py-2 px-3">
            <i className="bi bi-building"></i>
            <span>Mandant</span>
          </Badge>
        )}
      </div>

      <div className="d-flex align-items-center gap-3">
        {/* Current tenant indicator */}
        {isSuperAdmin && selectedTenant && (
          <Badge bg="light" text="dark" className="d-none d-lg-flex align-items-center gap-1 py-2 px-3">
            <i className="bi bi-check-circle-fill text-success"></i>
            <span>Aktiver Mandant: <strong>{selectedTenant.name}</strong></span>
          </Badge>
        )}

        <button className="btn btn-outline-secondary btn-sm" onClick={() => window.location.reload()}>
          <i className="bi bi-arrow-repeat me-1"></i>
          Reload
        </button>

        <Dropdown align="end">
          <Dropdown.Toggle variant="light" className="d-flex align-items-center gap-2">
            <i className="bi bi-person-circle fs-5"></i>
            <span className="d-none d-md-inline">{user?.username}</span>
            {isSuperAdmin && (
              <Badge bg="danger" className="ms-1">Admin</Badge>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Header>
              <div>{user?.username}</div>
              <small className="text-muted">{user?.email}</small>
              <div className="mt-1">
                <Badge bg={isSuperAdmin ? 'danger' : 'primary'}>
                  {isSuperAdmin ? 'Super Admin' : user?.role === 'tenant_admin' ? 'Tenant Admin' : 'Benutzer'}
                </Badge>
              </div>
            </Dropdown.Header>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => navigate('/settings')}>
              <i className="bi bi-gear me-2"></i>
              Einstellungen
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout} className="text-danger">
              <i className="bi bi-box-arrow-right me-2"></i>
              Abmelden
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  )
}
